import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WithdrawalAdminService } from '../../_core/_services/withdrawal-admin.service';
import { NotificationService } from '../../_core/_services/notification.service';
import {
  PaymentGatewayConfigDto,
  PayoutFieldRequirementDto,
  CreatePayoutFieldRequirementRequestDto,
  UpdatePayoutFieldRequirementRequestDto
} from '../../_core/_model/payment-admin';
import { MiniCountryDto } from '../../_core/_model/countries';
import { ApiService } from '../../_core/_services/api.service';

@Component({
  selector: 'app-withdrawal-payout-fields',
  standalone: false,
  templateUrl: './withdrawal-payout-fields.html',
  styleUrl: './withdrawal-payout-fields.css'
})
export class WithdrawalPayoutFields implements OnInit {
  loading = false;
  saving = false;

  error = '';

  countries: MiniCountryDto[] = [];

  fields: PayoutFieldRequirementDto[] = [];
  filteredFields: PayoutFieldRequirementDto[] = [];
  configs: PaymentGatewayConfigDto[] = [];

  filterJurisdictionCode = '';
  filterPaymentMethod = '';
  filterGatewayConfigId = '';

  showModal = false;
  editingId: string | null = null;
  editingItem: PayoutFieldRequirementDto | null = null;
  form!: FormGroup;

  readonly fieldTypeOptions = ['text', 'number', 'email', 'select', 'textarea'];
  readonly paymentMethodOptions = ['bank-transfer', 'ach', 'mobile-money', 'wallet', 'interac'];

  blaaizTemplates: Record<string, any[]> = {
    CAD: [
      {
        fieldKey: 'recipientEmail',
        label: 'Interac Email',
        fieldType: 'email',
        isRequired: true,
        placeholder: 'customer@email.com'
      },
      {
        fieldKey: 'recipientName',
        label: 'Recipient Name',
        fieldType: 'text',
        isRequired: true,
        placeholder: 'John Doe'
      }
    ],

    USD: [
      {
        fieldKey: 'accountName',
        label: 'Account Name',
        fieldType: 'text',
        isRequired: true
      },
      {
        fieldKey: 'accountNumber',
        label: 'Account Number',
        fieldType: 'text',
        isRequired: true
      },
      {
        fieldKey: 'routingNumber',
        label: 'Routing Number',
        fieldType: 'text',
        isRequired: true
      },
      {
        fieldKey: 'bankName',
        label: 'Bank Name',
        fieldType: 'text',
        isRequired: false
      },
      {
        fieldKey: 'accountType',
        label: 'Account Type',
        fieldType: 'select',
        isRequired: false,
        optionsJson: JSON.stringify([
          { value: 'checking', label: 'Checking' },
          { value: 'savings', label: 'Savings' }
        ], null, 2)
      }
    ],

    GBP: [
      {
        fieldKey: 'accountName',
        label: 'Account Name',
        fieldType: 'text',
        isRequired: true
      },
      {
        fieldKey: 'accountNumber',
        label: 'Account Number',
        fieldType: 'text',
        isRequired: true
      },
      {
        fieldKey: 'sortCode',
        label: 'Sort Code',
        fieldType: 'text',
        isRequired: true
      },
      {
        fieldKey: 'bankName',
        label: 'Bank Name',
        fieldType: 'text',
        isRequired: false
      }
    ],

    EUR: [
      {
        fieldKey: 'accountName',
        label: 'Account Name',
        fieldType: 'text',
        isRequired: true
      },
      {
        fieldKey: 'iban',
        label: 'IBAN',
        fieldType: 'text',
        isRequired: true
      },
      {
        fieldKey: 'bankName',
        label: 'Bank Name',
        fieldType: 'text',
        isRequired: false
      }
    ]
  };

  selectedBlaaizTemplateCurrency: string | null = null;
selectedBlaaizTemplateFieldIndex = 0;

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private withdrawalAdminService: WithdrawalAdminService,
    private notifyService: NotificationService
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.loadInitialData();
    this.loadMain();
  }


  loadMain() {
    this.loading = true;

    this.api.getMain().subscribe({
      next: (res) => {
        this.countries = res.data?.countries || [];
      },
      error: (e) => (this.error = e?.error?.Error?.Details ?? 'Failed to load main data'),
      complete: () => (this.loading = false),
    });
  }

  buildForm(): void {
    this.form = this.fb.group({
      jurisdictionCode: ['', [Validators.required]],
      paymentGatewayConfigId: [''],
      paymentMethod: ['bank-transfer'],
      fieldKey: ['', [Validators.required]],
      label: ['', [Validators.required]],
      fieldType: ['text', [Validators.required]],
      isRequired: [true],
      sortOrder: [1, [Validators.required, Validators.min(1)]],
      placeholder: [''],
      helpText: [''],
      optionsJson: [''],
      validationJson: [''],
      metadataJson: ['']
    });
  }

  loadInitialData(): void {
    this.loading = true;

    this.withdrawalAdminService.getConfigs().subscribe({
      next: (res) => {
        this.configs = res.data || [];
      },
      error: () => {
        this.notifyService.showError('Failed to load gateway configs', 'Error');
      }
    });

    this.loadFields();
  }

  loadFields(): void {
    this.loading = true;

    this.withdrawalAdminService.getPayoutFields(
      this.filterJurisdictionCode || undefined,
      this.filterGatewayConfigId || undefined,
      this.filterPaymentMethod || undefined
    ).subscribe({
      next: (res) => {
        this.fields = res.data || [];
        this.applyClientFilter();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to load payout fields',
          'Error'
        );
        this.fields = [];
        this.filteredFields = [];
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  applyClientFilter(): void {
    this.filteredFields = this.fields.filter((x) => {
      const jurisdictionOk = !this.filterJurisdictionCode ||
        x.jurisdictionCode.toLowerCase().includes(this.filterJurisdictionCode.toLowerCase());

      const methodOk = !this.filterPaymentMethod || x.paymentMethod === this.filterPaymentMethod;
      const gatewayOk = !this.filterGatewayConfigId || x.paymentGatewayConfigId === this.filterGatewayConfigId;

      return jurisdictionOk && methodOk && gatewayOk;
    });
  }

  openCreateModal(): void {
    this.editingId = null;
    this.editingItem = null;

    this.form.reset({
      jurisdictionCode: '',
      paymentGatewayConfigId: '',
      paymentMethod: 'bank-transfer',
      fieldKey: '',
      label: '',
      fieldType: 'text',
      isRequired: true,
      sortOrder: 1,
      placeholder: '',
      helpText: '',
      optionsJson: '',
      validationJson: '',
      metadataJson: ''
    });

    this.showModal = true;
  }

  openEditModal(item: PayoutFieldRequirementDto): void {
    this.editingId = item.id;
    this.editingItem = item;

    this.form.patchValue({
      jurisdictionCode: item.jurisdictionCode,
      paymentGatewayConfigId: item.paymentGatewayConfigId || '',
      paymentMethod: item.paymentMethod || '',
      fieldKey: item.fieldKey,
      label: item.label,
      fieldType: item.fieldType,
      isRequired: item.isRequired,
      sortOrder: item.sortOrder,
      placeholder: item.placeholder || '',
      helpText: item.helpText || '',
      optionsJson: item.optionsJson || '',
      validationJson: item.validationJson || '',
      metadataJson: item.metadataJson || ''
    });

    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.editingId = null;
    this.editingItem = null;
  }

  save(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.notifyService.showError('Please complete the form correctly.', 'Validation');
      return;
    }

    if (!this.isValidJsonOrEmpty(this.form.value.optionsJson)) {
      this.notifyService.showError('Options JSON is invalid.', 'Validation');
      return;
    }

    if (!this.isValidJsonOrEmpty(this.form.value.validationJson)) {
      this.notifyService.showError('Validation JSON is invalid.', 'Validation');
      return;
    }

    if (!this.isValidJsonOrEmpty(this.form.value.metadataJson)) {
      this.notifyService.showError('Metadata JSON is invalid.', 'Validation');
      return;
    }

    this.saving = true;

    const basePayload = {
      jurisdictionCode: String(this.form.value.jurisdictionCode || '').trim().toUpperCase(),
      paymentGatewayConfigId: this.toNullableString(this.form.value.paymentGatewayConfigId),
      paymentMethod: this.toNullableString(this.form.value.paymentMethod),
      fieldKey: String(this.form.value.fieldKey || '').trim(),
      label: String(this.form.value.label || '').trim(),
      fieldType: this.form.value.fieldType,
      isRequired: !!this.form.value.isRequired,
      sortOrder: Number(this.form.value.sortOrder || 1),
      placeholder: this.toNullableString(this.form.value.placeholder),
      helpText: this.toNullableString(this.form.value.helpText),
      optionsJson: this.normalizeJsonText(this.form.value.optionsJson),
      validationJson: this.normalizeJsonText(this.form.value.validationJson),
      metadataJson: this.normalizeJsonText(this.form.value.metadataJson)
    };

    const request$ = this.editingId
      ? this.withdrawalAdminService.updatePayoutField(this.editingId, {
        ...basePayload,
        isActive: this.editingItem?.isActive ?? true
      } as UpdatePayoutFieldRequirementRequestDto)
      : this.withdrawalAdminService.createPayoutField(
        basePayload as CreatePayoutFieldRequirementRequestDto
      );

    request$.subscribe({
      next: () => {
        this.notifyService.showSuccess(
          this.editingId ? 'Payout field updated successfully' : 'Payout field created successfully',
          'Success'
        );
        this.closeModal();
        this.loadFields();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to save payout field',
          'Error'
        );
      },
      complete: () => {
        this.saving = false;
      }
    });
  }

  deleteField(item: PayoutFieldRequirementDto): void {
    const confirmed = window.confirm(`Delete payout field "${item.label}" (${item.fieldKey})?`);
    if (!confirmed) return;

    this.withdrawalAdminService.deletePayoutField(item.id).subscribe({
      next: () => {
        this.notifyService.showSuccess('Payout field deleted successfully', 'Success');
        this.loadFields();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to delete payout field',
          'Error'
        );
      }
    });
  }

  toggleStatus(item: PayoutFieldRequirementDto): void {
    this.withdrawalAdminService.togglePayoutFieldStatus(item.id).subscribe({
      next: () => {
        this.notifyService.showSuccess('Payout field status updated', 'Success');
        this.loadFields();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to update payout field status',
          'Error'
        );
      }
    });
  }

  getConfigName(id?: string | null): string {
    if (!id) return 'All Gateways';
    const cfg = this.configs.find(x => x.id === id);
    return cfg ? `${cfg.providerName} - ${cfg.name}` : id;
  }

  badgeClass(isActive: boolean): string {
    return isActive ? 'text-bg-success' : 'text-bg-secondary';
  }

  insertNgBankSelectTemplate(): void {
    this.form.patchValue({
      fieldType: 'select',
      optionsJson: this.prettyJson({
        type: 'lookup',
        lookupType: 'institution',
        filters: {
          countryCode: 'NG',
          providerCode: 'FLUTTERWAVE'
        },
        valueField: 'value',
        labelField: 'label'
      })
    });
  }

  insertStaticSelectTemplate(): void {
    this.form.patchValue({
      fieldType: 'select',
      optionsJson: this.prettyJson({
        type: 'static',
        options: [
          { value: 'savings', label: 'Savings' },
          { value: 'current', label: 'Current' }
        ]
      })
    });
  }

  insertAccountNumberValidationTemplate(): void {
    this.form.patchValue({
      fieldType: 'text',
      validationJson: this.prettyJson({
        minLength: 10,
        maxLength: 10,
        pattern: '^[0-9]+$',
        message: 'Account number must be 10 digits'
      }),
      metadataJson: this.prettyJson({
        resolve: {
          type: 'accountName',
          triggerFields: ['bankCode', 'accountNumber'],
          resultField: 'accountName'
        }
      })
    });
  }

  insertResolvedAccountNameTemplate(): void {
    this.form.patchValue({
      fieldType: 'text',
      metadataJson: this.prettyJson({
        readonly: true,
        resolved: true
      })
    });
  }

  insertEmailValidationTemplate(): void {
    this.form.patchValue({
      fieldType: 'email',
      validationJson: this.prettyJson({
        type: 'email',
        maxLength: 150,
        message: 'Enter a valid email address'
      })
    });
  }

  formatOptionsJson(): void {
    this.patchFormattedJson('optionsJson');
  }

  formatValidationJson(): void {
    this.patchFormattedJson('validationJson');
  }

  formatMetadataJson(): void {
    this.patchFormattedJson('metadataJson');
  }

  private patchFormattedJson(controlName: 'optionsJson' | 'validationJson' | 'metadataJson'): void {
    const value = this.form.value[controlName];
    if (!value) return;

    try {
      const parsed = JSON.parse(String(value));
      this.form.patchValue({
        [controlName]: this.prettyJson(parsed)
      });
    } catch {
      this.notifyService.showError(`${this.labelForControl(controlName)} is invalid JSON`, 'Validation');
    }
  }

  private labelForControl(controlName: string): string {
    switch (controlName) {
      case 'optionsJson': return 'Options JSON';
      case 'validationJson': return 'Validation JSON';
      case 'metadataJson': return 'Metadata JSON';
      default: return 'JSON';
    }
  }

  private prettyJson(value: unknown): string {
    return JSON.stringify(value, null, 2);
  }

  private isValidJsonOrEmpty(value: unknown): boolean {
    const text = this.toNullableString(value);
    if (!text) return true;

    try {
      JSON.parse(text);
      return true;
    } catch {
      return false;
    }
  }

  private normalizeJsonText(value: unknown): string | null {
    const text = this.toNullableString(value);
    if (!text) return null;

    try {
      return JSON.stringify(JSON.parse(text));
    } catch {
      return text;
    }
  }

  private toNullableString(value: unknown): string | null {
    const text = String(value ?? '').trim();
    return text ? text : null;
  }

  getCurrentBlaaizTemplateFields(): any[] {
  if (!this.selectedBlaaizTemplateCurrency) {
    return [];
  }

  return this.blaaizTemplates[this.selectedBlaaizTemplateCurrency] || [];
}

get currentBlaaizTemplateFieldNumber(): number {
  return Number(this.form?.get('sortOrder')?.value || 1);
}

applyBlaaizFieldTemplate(currency: string): void {
  this.selectedBlaaizTemplateCurrency = currency;
  this.selectedBlaaizTemplateFieldIndex = 0;

  this.applyCurrentBlaaizField();
}
applyCurrentBlaaizField(): void {
  const fields = this.getCurrentBlaaizTemplateFields();

  if (!fields.length) {
    return;
  }

  const current = fields[this.selectedBlaaizTemplateFieldIndex];

  if (!current) {
    return;
  }

  this.form.patchValue({
    fieldKey: current.fieldKey,
    label: current.label,
    fieldType: current.fieldType,
    isRequired: current.isRequired,
    sortOrder: this.selectedBlaaizTemplateFieldIndex + 1,
    placeholder: current.placeholder || '',
    optionsJson: current.optionsJson || ''
  });
}

nextBlaaizTemplateField(): void {
  const fields = this.getCurrentBlaaizTemplateFields();

  if (!fields.length) {
    return;
  }

  this.selectedBlaaizTemplateFieldIndex++;

  if (this.selectedBlaaizTemplateFieldIndex >= fields.length) {
    this.selectedBlaaizTemplateFieldIndex = 0;
  }

  this.applyCurrentBlaaizField();
}
}
